import React, { useState, useMemo } from "react";
import { BookingsData } from "../../utils/apis/bookings_repository";
import { getClosestMonday, getWeekDates } from "../../utils/helper";
import { Popover, PopoverTrigger, PopoverContent } from "./Popover";

interface WeekViewBarProps {
    bookings: BookingsData[];
    totalCapacity: number;
    selectedDay: string | null;
    onDaySelect: (day: string | null) => void;
}

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const formatDate = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};

const WeekViewBar: React.FC<WeekViewBarProps> = ({
    bookings,
    totalCapacity,
    selectedDay,
    onDaySelect,
}) => {
    const [weekOffset, setWeekOffset] = useState(0);

    const weekDates = useMemo(() => {
        const today = new Date();
        const monday = getClosestMonday(new Date(today));
        monday.setDate(monday.getDate() + weekOffset * 7);
        return getWeekDates(monday);
    }, [weekOffset]);

    const bookingsPerDay = useMemo(() => {
        const map: Record<string, BookingsData[]> = {};
        bookings.forEach((b) => {
            const dateKey = b.startTime.split("T")[0];
            if (!map[dateKey]) map[dateKey] = [];
            map[dateKey].push(b);
        });
        return map;
    }, [bookings]);

    const goToToday = () => setWeekOffset(0);

    const weekLabel = useMemo(() => {
        if (weekDates.length === 0) return "";
        const mon = weekDates[0];
        const sun = weekDates[6];
        const fmt = (d: Date) =>
            d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
        return `${fmt(mon)} - ${fmt(sun)}`;
    }, [weekDates]);

    return (
        <div className="mb-4">
            {/* Header row */}
            <div className="flex items-center mb-2">
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setWeekOffset((o) => Math.max(0, o - 1))}
                        disabled={weekOffset <= 0}
                        className={`px-2 py-1 rounded border text-sm ${weekOffset <= 0 ? 'border-gray-200 text-gray-300 cursor-not-allowed' : 'border-gray-300 hover:bg-gray-100'}`}
                    >
                        &larr;
                    </button>
                    <span className="text-sm font-medium text-gray-600">
                        {weekLabel}
                    </span>
                    <button
                        onClick={() => setWeekOffset((o) => o + 1)}
                        className="px-2 py-1 rounded border border-gray-300 hover:bg-gray-100 text-sm"
                    >
                        &rarr;
                    </button>
                    {weekOffset !== 0 && (
                        <button
                            onClick={goToToday}
                            className="px-3 py-1 rounded border border-blue-400 text-blue-600 hover:bg-blue-50 text-sm"
                        >
                            Today
                        </button>
                    )}
                </div>
            </div>

            {/* Day cells */}
            <div className="grid grid-cols-7 gap-1">
                {weekDates.map((date, i) => {
                    const dateKey = formatDate(date);
                    const dayBookings = bookingsPerDay[dateKey] || [];
                    const count = dayBookings.length;
                    const pct =
                        totalCapacity > 0
                            ? Math.min((count / totalCapacity) * 100, 100)
                            : 0;

                    let barColor = "bg-green-500";
                    if (pct >= 100) barColor = "bg-red-500";
                    else if (pct >= 75) barColor = "bg-yellow-500";

                    const isSelected = selectedDay === dateKey;
                    const isToday = formatDate(new Date()) === dateKey;

                    return (
                        <Popover key={dateKey}>
                            <PopoverTrigger asChild>
                                <button
                                    onClick={() =>
                                        onDaySelect(isSelected ? null : dateKey)
                                    }
                                    className={`
                                        flex flex-col items-center p-2 rounded-lg border text-xs transition-all cursor-pointer
                                        ${isSelected ? "border-blue-500 bg-blue-50 ring-1 ring-blue-500" : "border-gray-200 hover:border-gray-400"}
                                        ${isToday && !isSelected ? "border-blue-300" : ""}
                                    `}
                                >
                                    <span className="font-semibold text-gray-700">
                                        {DAY_NAMES[i]}
                                    </span>
                                    <span className="text-gray-500">
                                        {date.getDate()}
                                    </span>
                                    <span
                                        className={`mt-1 font-medium ${
                                            pct >= 100
                                                ? "text-red-600"
                                                : pct >= 75
                                                  ? "text-yellow-600"
                                                  : "text-gray-600"
                                        }`}
                                    >
                                        {count} / {totalCapacity}
                                    </span>
                                    {/* Progress bar */}
                                    <div className="w-full h-1.5 bg-gray-200 rounded-full mt-1">
                                        <div
                                            className={`h-full rounded-full ${barColor}`}
                                            style={{ width: `${pct}%` }}
                                        />
                                    </div>
                                </button>
                            </PopoverTrigger>
                            <PopoverContent
                                className="bg-white p-3 max-h-60 overflow-y-auto"
                                side="bottom"
                            >
                                <div className="text-sm font-semibold mb-2">
                                    {DAY_NAMES[i]},{" "}
                                    {date.toLocaleDateString("en-US", {
                                        month: "short",
                                        day: "numeric",
                                    })}
                                    {" - "}
                                    {count} booking{count !== 1 ? "s" : ""}
                                </div>
                                {dayBookings.length === 0 ? (
                                    <p className="text-xs text-gray-500">
                                        No bookings
                                    </p>
                                ) : (
                                    <ul className="space-y-1">
                                        {dayBookings.map((b) => (
                                            <li
                                                key={b.bookingId}
                                                className="text-xs text-gray-700 truncate"
                                            >
                                                {b.name || b.email}
                                                <span className="text-gray-400 ml-1">
                                                    ({b.tenant})
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </PopoverContent>
                        </Popover>
                    );
                })}
            </div>
        </div>
    );
};

export default WeekViewBar;
